import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { AnalyticsHooks } from '@frontend/analytics-shared';
import { humanizeDateDifference } from '@frontend/shared';
import { CALL_INTEL_TIME_PERIODS, DEFAULT_CALL_INTEL_TIME_PERIOD } from '../../../constants';
import { useCallIntelLocations } from '../../../hooks/use-call-intel-locations';
import { useCallIntelFiltersContext } from '../../context/call-intel-filters.context';

export const useTimePeriodFilterData = () => {
  const { filterValues, isLoading, updateFilterValues, updateFilterModified, setAutoResetFilters } =
    useCallIntelFiltersContext();
  const { preselectedAccessibleLocations } = useCallIntelLocations();

  const defaultLocations = useMemo(
    () => Object.keys(preselectedAccessibleLocations || {}),
    [preselectedAccessibleLocations]
  );
  const selectedLocations = useMemo(() => filterValues.locations || [], [filterValues.locations]);

  const { defaultEndDate, defaultStartDate, timePeriods } = AnalyticsHooks.useTimePeriodPopoverSelector({
    defaultPeriod: DEFAULT_CALL_INTEL_TIME_PERIOD,
    skipFindingExistingPeriod: true,
    timePeriods: CALL_INTEL_TIME_PERIODS,
    values: {
      endDate: filterValues.endDate,
      selectedPeriod: filterValues.selectedTimePeriodKey,
      startDate: filterValues.startDate,
    },
  });

  useEffect(() => {
    if (!filterValues.startDate && !filterValues.endDate) {
      updateFilterValues({
        startDate: defaultStartDate,
        endDate: defaultEndDate,
        selectedTimePeriodKey: DEFAULT_CALL_INTEL_TIME_PERIOD,
      });
    }
  }, [defaultEndDate, defaultStartDate, filterValues.endDate, filterValues.startDate, updateFilterValues]);

  useEffect(() => {
    const isStartDateModified = !dayjs(filterValues.startDate).isSame(dayjs(defaultStartDate), 'date');
    const isEndDateModified = !dayjs(filterValues.endDate).isSame(dayjs(defaultEndDate), 'date');

    updateFilterModified({ startDate: isStartDateModified, endDate: isEndDateModified });
  }, [updateFilterModified, filterValues.endDate, filterValues.startDate, defaultStartDate, defaultEndDate]);

  const filterHintText = useMemo(() => {
    if (filterValues.selectedTimePeriodKey) {
      return timePeriods[filterValues.selectedTimePeriodKey]?.label ?? '';
    }
    if (filterValues.startDate && filterValues.endDate) {
      return humanizeDateDifference(filterValues.startDate, filterValues.endDate, true);
    }
    return '';
  }, [filterValues.selectedTimePeriodKey, filterValues.startDate, filterValues.endDate, timePeriods]);

  return {
    filterValues,
    filterHintText,
    defaultStartDate,
    defaultEndDate,
    updateFilterValues,
    selectedLocations,
    totalLocationsCount: defaultLocations.length,
    isLoading,
    setAutoResetFilters,
  };
};
